import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import Input from '../../../../components/ui/Input';
import Button from '../../../../components/ui/Button';
import { markMudadUploaded } from '../../../../api/Payroll/wps';
import { appMonthNameYear } from '../../../../utils/appDateFormat';

interface Props {
  open: boolean;
  year: number;
  month: number;
  onClose: () => void;
  onSuccess: () => void;
}

const periodLabel = (year: number, month: number) =>
  appMonthNameYear(new Date(year, month - 1, 1));

export const MarkUploadedModal: React.FC<Props> = ({ open, year, month, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [referenceError, setReferenceError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    let ok = true;
    const trimmed = reference.trim();
    if (trimmed.length === 0) {
      setReferenceError(t('payroll.complianceCenter.ksaWps.mudadReferenceRequired', 'Mudad reference is required'));
      ok = false;
    } else if (trimmed.length > 64) {
      setReferenceError(t('payroll.complianceCenter.ksaWps.mudadReferenceLength', 'Mudad reference must be 1–64 characters'));
      ok = false;
    } else {
      setReferenceError(null);
    }
    if ((note ?? '').length > 500) {
      setNoteError(t('payroll.complianceCenter.ksaWps.noteLength', 'Note must be at most 500 characters'));
      ok = false;
    } else {
      setNoteError(null);
    }
    return ok;
  };

  const reset = () => {
    setReference('');
    setNote('');
    setReferenceError(null);
    setNoteError(null);
    setSubmitError(null);
  };

  const handleConfirm = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await markMudadUploaded(year, month, {
        mudadReference: reference.trim(),
        note: note.trim() || undefined,
      });
      if (result.errors.length > 0) {
        const first = result.errors[0];
        const msg = first.description ?? first.code ?? '';
        if (/mudadReference/i.test(msg) || /mudadReference/i.test(first.path ?? '')) {
          setReferenceError(t(msg, msg));
        } else if (/note/i.test(msg) || /note/i.test(first.path ?? '')) {
          setNoteError(t(msg, msg));
        } else {
          setSubmitError(
            t(msg || 'payroll.complianceCenter.ksaWps.markUploadedFail', msg || 'Could not mark as uploaded.'),
          );
        }
        return;
      }
      reset();
      onSuccess();
      onClose();
    } catch {
      setSubmitError(t('payroll.complianceCenter.ksaWps.markUploadedFail', 'Could not mark as uploaded.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>
            {t('payroll.complianceCenter.ksaWps.markModalTitle', 'Mark Uploaded to Mudad')}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              'payroll.complianceCenter.ksaWps.markModalIntro',
              "Confirm you've uploaded the file at mudad.com.sa for {{period}}.",
              { period: periodLabel(year, month) },
            )}
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1" htmlFor="mudad-ref">
                {t('payroll.complianceCenter.ksaWps.mudadReferenceLabel', 'Mudad reference')}
                <span className="text-destructive ms-1">*</span>
              </label>
              <Input
                id="mudad-ref"
                value={reference}
                maxLength={64}
                aria-invalid={!!referenceError}
                aria-describedby="mudad-ref-hint"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setReference(e.target.value);
                  if (referenceError) setReferenceError(null);
                }}
              />
              <p id="mudad-ref-hint" className="text-xs text-muted-foreground mt-1">
                {t(
                  'payroll.complianceCenter.ksaWps.mudadReferenceHint',
                  'The reference number Mudad returned after accepting your upload.',
                )}
              </p>
              {referenceError && (
                <p role="alert" className="text-xs text-destructive mt-1">{referenceError}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium block mb-1" htmlFor="mudad-note">
                {t('payroll.complianceCenter.ksaWps.noteLabel', 'Note (optional)')}
              </label>
              <textarea
                id="mudad-note"
                rows={3}
                maxLength={500}
                aria-invalid={!!noteError}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  if (noteError) setNoteError(null);
                }}
              />
              {noteError && (
                <p role="alert" className="text-xs text-destructive mt-1">{noteError}</p>
              )}
            </div>

            {submitError && (
              <p role="alert" className="text-sm text-destructive">{submitError}</p>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel} disabled={submitting}>
            {t('payroll.complianceCenter.ksaWps.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={submitting} loading={submitting}>
            {submitting
              ? t('common.saving', 'Saving...')
              : t('payroll.complianceCenter.ksaWps.confirmUpload', 'Confirm Upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkUploadedModal;
